import { AssistantContext } from '~/steps/steps';
import {
  FormField,
  FormMultipleChoiceComponentConfig,
  FormValueType,
  StepDefinition,
} from '@/interfaces/step-definition';
import { FormFieldType } from '@/consts/input-types';
import { StepTypes } from '@/consts/step-types';
import { getFormattedFieldData } from '@/lib/formatter/context';
import i18n from '@/plugins/i18n';

const isEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  return false;
};

// Translate one MultipleChoice option value via the same i18n keys the
// comment formatter uses. Falls back to the raw option value if no
// translation key is registered.
const translateOption = (
  step: StepDefinition,
  field: FormField,
  optionValue: string,
): string => {
  const key = `${step.id}.formFields.${field.name}.options.${optionValue}.label`;
  return i18n.te(key) ? (i18n.t(key) as string) : optionValue;
};

const getTranslatedValue = (
  step: StepDefinition,
  field: FormField,
  rawValue: unknown,
  stepData: Record<string, unknown>,
  assistantContext: AssistantContext,
): unknown => {
  // MultipleChoice: translate each option. Multi-select fields stay as array
  // (["LAN","WLAN"]). Single-answer fields (singleAnswer: true) get unwrapped
  // to a plain string — they're conceptually "pick one", not "pick many",
  // even though the form stores them as a one-element array.
  if (field.type === FormFieldType.MultipleChoice && Array.isArray(rawValue)) {
    const translated = rawValue.map((v) =>
      translateOption(step, field, String(v)),
    );
    const singleAnswer = (field.component as FormMultipleChoiceComponentConfig)
      ?.singleAnswer;
    return singleAnswer ? translated[0] : translated;
  }
  // Everything else (TextField, Checkbox, Switch, ...): defer to the formatter
  // chain — including the step's outputFormatter — so values are produced
  // exactly as the summary/comment shows them.
  const formatted = getFormattedFieldData(
    field,
    assistantContext,
    stepData as { [key: string]: FormValueType },
    step,
  );
  return formatted.formattedValue;
};

// Builds a flat record of the assistant answers for the product request.
// Keys are the untranslated field names (e.g. "lisArea"), values are
// translated/formatted. The contact-form step is excluded — that data already
// lives in the dedicated ProductRequest fields.
export const getRequestAssistantData = (
  steps: StepDefinition[],
  assistantContext: AssistantContext,
): Record<string, unknown> => {
  const data: Record<string, unknown> = {};

  const relevantSteps = steps.filter(
    (step) =>
      step.type !== StepTypes.CONTACT_FORM &&
      step.fields &&
      step.fields.length > 0 &&
      !(step.isDisabled && step.isDisabled(assistantContext)),
  );

  relevantSteps.forEach((step) => {
    const stepData = (
      assistantContext as unknown as Record<string, Record<string, unknown>>
    )[step.id];
    if (!stepData) {
      return;
    }

    step.fields?.forEach((field) => {
      const value = stepData[field.name];
      if (isEmpty(value)) {
        return;
      }
      data[field.name] = getTranslatedValue(
        step,
        field,
        value,
        stepData,
        assistantContext,
      );
    });
  });

  return data;
};
